import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as GoogleAdsService from '@/services/GoogleAdsService';
import type { DateRangeOption, ExportDownload, ExportOptions } from '@/types/googleAds';

const QUERY_KEYS = {
  connection: ['googleAds', 'connection'] as const,
  accounts: ['googleAds', 'accounts'] as const,
  account: (customerId: string) => ['googleAds', 'account', customerId] as const,
  campaigns: (customerId: string, dateRange?: string) =>
    ['googleAds', 'campaigns', customerId, dateRange] as const,
  assetGroups: (customerId: string, campaignId?: string) =>
    ['googleAds', 'assetGroups', customerId, campaignId] as const,
  assets: (customerId: string, type?: string) => ['googleAds', 'assets', customerId, type] as const,
  ads: (customerId: string, campaignId?: string, adGroupId?: string) =>
    ['googleAds', 'ads', customerId, campaignId, adGroupId] as const,
  headlines: (customerId: string) => ['googleAds', 'headlines', customerId] as const,
  descriptions: (customerId: string) => ['googleAds', 'descriptions', customerId] as const,
  images: (customerId: string) => ['googleAds', 'images', customerId] as const,
  videos: (customerId: string) => ['googleAds', 'videos', customerId] as const,
  keywords: (customerId: string, campaignId?: string, adGroupId?: string) =>
    ['googleAds', 'keywords', customerId, campaignId, adGroupId] as const,
  negativeKeywords: (customerId: string, campaignId?: string) =>
    ['googleAds', 'negativeKeywords', customerId, campaignId] as const,
  searchTerms: (customerId: string, dateRange?: string, campaignId?: string) =>
    ['googleAds', 'searchTerms', customerId, dateRange, campaignId] as const,
  locations: (customerId: string, campaignId?: string) =>
    ['googleAds', 'locations', customerId, campaignId] as const,
  audiences: (customerId: string, campaignId?: string) =>
    ['googleAds', 'audiences', customerId, campaignId] as const,
  recommendations: (customerId: string) => ['googleAds', 'recommendations', customerId] as const,
  recommendationImpact: (customerId: string) => ['googleAds', 'recommendationImpact', customerId] as const,
  conversionActions: (customerId: string) => ['googleAds', 'conversionActions', customerId] as const,
  conversionStats: (customerId: string, dateRange?: string) =>
    ['googleAds', 'conversionStats', customerId, dateRange] as const,
  budgets: (customerId: string) => ['googleAds', 'budgets', customerId] as const,
  biddingStrategies: (customerId: string) => ['googleAds', 'biddingStrategies', customerId] as const,
  extensions: (customerId: string, type?: string) => ['googleAds', 'extensions', customerId, type] as const,
  landingPages: (customerId: string) => ['googleAds', 'landingPages', customerId] as const,
  performance: (customerId: string, dateRange: string) =>
    ['googleAds', 'performance', customerId, dateRange] as const,
  qualitySignals: (customerId: string) => ['googleAds', 'qualitySignals', customerId] as const,
  labels: (customerId: string) => ['googleAds', 'labels', customerId] as const,
  shopping: (customerId: string) => ['googleAds', 'shopping', customerId] as const,
  changeHistory: (customerId: string, months?: number) =>
    ['googleAds', 'changeHistory', customerId, months] as const,
  auditSummary: (customerId: string, dateRange?: string) =>
    ['googleAds', 'auditSummary', customerId, dateRange] as const,
  exportProgress: (jobId: string) => ['googleAds', 'export', jobId] as const,
};

// Connection & OAuth
export function useGoogleAdsConnection() {
  return useQuery({
    queryKey: QUERY_KEYS.connection,
    queryFn: GoogleAdsService.getConnectionStatus,
    staleTime: 30 * 1000,
    retry: false,
  });
}

export function useInitiateOAuth() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: GoogleAdsService.initiateOAuth,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.connection });
    },
  });
}

export function useCompleteOAuth() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ code, state }: { code: string; state: string }) =>
      GoogleAdsService.completeOAuth(code, state),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.connection });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.accounts });
    },
  });
}

export function useDisconnectGoogleAds() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: GoogleAdsService.disconnectAccount,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['googleAds'] });
    },
  });
}

// Accounts
export function useGoogleAdsAccounts() {
  const { data: connection } = useGoogleAdsConnection();
  return useQuery({
    queryKey: QUERY_KEYS.accounts,
    queryFn: GoogleAdsService.listAccounts,
    enabled: connection?.connected === true,
  });
}

export function useGoogleAdsAccount(customerId: string | undefined) {
  const { data: connection } = useGoogleAdsConnection();
  return useQuery({
    queryKey: QUERY_KEYS.account(customerId ?? ''),
    queryFn: () => GoogleAdsService.getAccount(customerId!),
    enabled: connection?.connected === true && !!customerId,
  });
}

// Campaigns
export function useGoogleAdsCampaigns(customerId: string | undefined, dateRange?: DateRangeOption) {
  const { data: connection } = useGoogleAdsConnection();
  return useQuery({
    queryKey: QUERY_KEYS.campaigns(customerId ?? '', dateRange),
    queryFn: () => GoogleAdsService.getCampaigns(customerId!, dateRange),
    enabled: connection?.connected === true && !!customerId,
  });
}

// Asset Groups
export function useGoogleAdsAssetGroups(customerId: string | undefined, campaignId?: string) {
  const { data: connection } = useGoogleAdsConnection();
  return useQuery({
    queryKey: QUERY_KEYS.assetGroups(customerId ?? '', campaignId),
    queryFn: () => GoogleAdsService.getAssetGroups(customerId!, campaignId),
    enabled: connection?.connected === true && !!customerId,
  });
}

// Assets
export function useGoogleAdsAssets(customerId: string | undefined, type?: string) {
  const { data: connection } = useGoogleAdsConnection();
  return useQuery({
    queryKey: QUERY_KEYS.assets(customerId ?? '', type),
    queryFn: () => GoogleAdsService.getAssets(customerId!, type),
    enabled: connection?.connected === true && !!customerId,
  });
}

// Ads
export function useGoogleAdsAds(customerId: string | undefined, campaignId?: string, adGroupId?: string) {
  const { data: connection } = useGoogleAdsConnection();
  return useQuery({
    queryKey: QUERY_KEYS.ads(customerId ?? '', campaignId, adGroupId),
    queryFn: () => GoogleAdsService.getAds(customerId!, campaignId, adGroupId),
    enabled: connection?.connected === true && !!customerId,
  });
}

// Headlines
export function useGoogleAdsHeadlines(customerId: string | undefined) {
  const { data: connection } = useGoogleAdsConnection();
  return useQuery({
    queryKey: QUERY_KEYS.headlines(customerId ?? ''),
    queryFn: () => GoogleAdsService.getHeadlines(customerId!),
    enabled: connection?.connected === true && !!customerId,
  });
}

// Descriptions
export function useGoogleAdsDescriptions(customerId: string | undefined) {
  const { data: connection } = useGoogleAdsConnection();
  return useQuery({
    queryKey: QUERY_KEYS.descriptions(customerId ?? ''),
    queryFn: () => GoogleAdsService.getDescriptions(customerId!),
    enabled: connection?.connected === true && !!customerId,
  });
}

// Images
export function useGoogleAdsImages(customerId: string | undefined) {
  const { data: connection } = useGoogleAdsConnection();
  return useQuery({
    queryKey: QUERY_KEYS.images(customerId ?? ''),
    queryFn: () => GoogleAdsService.getImages(customerId!),
    enabled: connection?.connected === true && !!customerId,
  });
}

// Videos
export function useGoogleAdsVideos(customerId: string | undefined) {
  const { data: connection } = useGoogleAdsConnection();
  return useQuery({
    queryKey: QUERY_KEYS.videos(customerId ?? ''),
    queryFn: () => GoogleAdsService.getVideos(customerId!),
    enabled: connection?.connected === true && !!customerId,
  });
}

// Keywords
export function useGoogleAdsKeywords(
  customerId: string | undefined,
  campaignId?: string,
  adGroupId?: string,
) {
  const { data: connection } = useGoogleAdsConnection();
  return useQuery({
    queryKey: QUERY_KEYS.keywords(customerId ?? '', campaignId, adGroupId),
    queryFn: () => GoogleAdsService.getKeywords(customerId!, campaignId, adGroupId),
    enabled: connection?.connected === true && !!customerId,
  });
}

// Negative Keywords
export function useGoogleAdsNegativeKeywords(customerId: string | undefined, campaignId?: string) {
  const { data: connection } = useGoogleAdsConnection();
  return useQuery({
    queryKey: QUERY_KEYS.negativeKeywords(customerId ?? '', campaignId),
    queryFn: () => GoogleAdsService.getNegativeKeywords(customerId!, campaignId),
    enabled: connection?.connected === true && !!customerId,
  });
}

// Search Terms
export function useGoogleAdsSearchTerms(
  customerId: string | undefined,
  dateRange?: string,
  campaignId?: string,
) {
  const { data: connection } = useGoogleAdsConnection();
  return useQuery({
    queryKey: QUERY_KEYS.searchTerms(customerId ?? '', dateRange, campaignId),
    queryFn: () => GoogleAdsService.getSearchTerms(customerId!, dateRange, campaignId),
    enabled: connection?.connected === true && !!customerId,
  });
}

// Locations
export function useGoogleAdsLocations(customerId: string | undefined, campaignId?: string) {
  const { data: connection } = useGoogleAdsConnection();
  return useQuery({
    queryKey: QUERY_KEYS.locations(customerId ?? '', campaignId),
    queryFn: () => GoogleAdsService.getLocationTargets(customerId!, campaignId),
    enabled: connection?.connected === true && !!customerId,
  });
}

// Audiences
export function useGoogleAdsAudiences(customerId: string | undefined, campaignId?: string) {
  const { data: connection } = useGoogleAdsConnection();
  return useQuery({
    queryKey: QUERY_KEYS.audiences(customerId ?? '', campaignId),
    queryFn: () => GoogleAdsService.getAudiences(customerId!, campaignId),
    enabled: connection?.connected === true && !!customerId,
  });
}

// Recommendations
export function useGoogleAdsRecommendations(customerId: string | undefined) {
  const { data: connection } = useGoogleAdsConnection();
  return useQuery({
    queryKey: QUERY_KEYS.recommendations(customerId ?? ''),
    queryFn: () => GoogleAdsService.getRecommendations(customerId!),
    enabled: connection?.connected === true && !!customerId,
  });
}

// Recommendation Impact
export function useGoogleAdsRecommendationImpact(customerId: string | undefined) {
  const { data: connection } = useGoogleAdsConnection();
  return useQuery({
    queryKey: QUERY_KEYS.recommendationImpact(customerId ?? ''),
    queryFn: () => GoogleAdsService.getRecommendationImpact(customerId!),
    enabled: connection?.connected === true && !!customerId,
  });
}

// Conversion Actions
export function useGoogleAdsConversionActions(customerId: string | undefined) {
  const { data: connection } = useGoogleAdsConnection();
  return useQuery({
    queryKey: QUERY_KEYS.conversionActions(customerId ?? ''),
    queryFn: () => GoogleAdsService.getConversionActions(customerId!),
    enabled: connection?.connected === true && !!customerId,
  });
}

// Conversion Stats
export function useGoogleAdsConversionStats(customerId: string | undefined, dateRange?: string) {
  const { data: connection } = useGoogleAdsConnection();
  return useQuery({
    queryKey: QUERY_KEYS.conversionStats(customerId ?? '', dateRange),
    queryFn: () => GoogleAdsService.getConversionStats(customerId!, dateRange),
    enabled: connection?.connected === true && !!customerId,
  });
}

// Budgets
export function useGoogleAdsBudgets(customerId: string | undefined) {
  const { data: connection } = useGoogleAdsConnection();
  return useQuery({
    queryKey: QUERY_KEYS.budgets(customerId ?? ''),
    queryFn: () => GoogleAdsService.getBudgets(customerId!),
    enabled: connection?.connected === true && !!customerId,
  });
}

// Bidding Strategies
export function useGoogleAdsBiddingStrategies(customerId: string | undefined) {
  const { data: connection } = useGoogleAdsConnection();
  return useQuery({
    queryKey: QUERY_KEYS.biddingStrategies(customerId ?? ''),
    queryFn: () => GoogleAdsService.getBiddingStrategies(customerId!),
    enabled: connection?.connected === true && !!customerId,
  });
}

// Extensions
export function useGoogleAdsExtensions(customerId: string | undefined, type?: string) {
  const { data: connection } = useGoogleAdsConnection();
  return useQuery({
    queryKey: QUERY_KEYS.extensions(customerId ?? '', type),
    queryFn: () => GoogleAdsService.getExtensions(customerId!, type),
    enabled: connection?.connected === true && !!customerId,
  });
}

// Landing Pages
export function useGoogleAdsLandingPages(customerId: string | undefined) {
  const { data: connection } = useGoogleAdsConnection();
  return useQuery({
    queryKey: QUERY_KEYS.landingPages(customerId ?? ''),
    queryFn: () => GoogleAdsService.getLandingPages(customerId!),
    enabled: connection?.connected === true && !!customerId,
  });
}

// Performance
export function useGoogleAdsPerformance(customerId: string | undefined, dateRange: DateRangeOption) {
  const { data: connection } = useGoogleAdsConnection();
  return useQuery({
    queryKey: QUERY_KEYS.performance(customerId ?? '', dateRange),
    queryFn: () => GoogleAdsService.getPerformance(customerId!, dateRange),
    enabled: connection?.connected === true && !!customerId,
  });
}

// Quality Signals
export function useGoogleAdsQualitySignals(customerId: string | undefined) {
  const { data: connection } = useGoogleAdsConnection();
  return useQuery({
    queryKey: QUERY_KEYS.qualitySignals(customerId ?? ''),
    queryFn: () => GoogleAdsService.getQualitySignals(customerId!),
    enabled: connection?.connected === true && !!customerId,
  });
}

// Labels
export function useGoogleAdsLabels(customerId: string | undefined) {
  const { data: connection } = useGoogleAdsConnection();
  return useQuery({
    queryKey: QUERY_KEYS.labels(customerId ?? ''),
    queryFn: () => GoogleAdsService.getLabels(customerId!),
    enabled: connection?.connected === true && !!customerId,
  });
}

// Shopping
export function useGoogleAdsShopping(customerId: string | undefined) {
  const { data: connection } = useGoogleAdsConnection();
  return useQuery({
    queryKey: QUERY_KEYS.shopping(customerId ?? ''),
    queryFn: () => GoogleAdsService.getShoppingProducts(customerId!),
    enabled: connection?.connected === true && !!customerId,
  });
}

// Change History
export function useGoogleAdsChangeHistory(customerId: string | undefined, months?: number) {
  const { data: connection } = useGoogleAdsConnection();
  return useQuery({
    queryKey: QUERY_KEYS.changeHistory(customerId ?? '', months),
    queryFn: () => GoogleAdsService.getChangeHistory(customerId!, months),
    enabled: connection?.connected === true && !!customerId,
  });
}

// Audit Summary
export function useGoogleAdsAuditSummary(customerId: string | undefined, dateRange?: string) {
  const { data: connection } = useGoogleAdsConnection();
  return useQuery({
    queryKey: QUERY_KEYS.auditSummary(customerId ?? '', dateRange),
    queryFn: () => GoogleAdsService.getAuditSummary(customerId!, dateRange),
    enabled: connection?.connected === true && !!customerId,
  });
}

// Export
export function useStartExport() {
  return useMutation({
    mutationFn: (options: ExportOptions) => GoogleAdsService.startExport(options),
  });
}

export function useExportProgress(jobId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: QUERY_KEYS.exportProgress(jobId ?? ''),
    queryFn: () => GoogleAdsService.getExportProgress(jobId!),
    enabled: enabled && !!jobId,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (data?.status === 'COMPLETED' || data?.status === 'FAILED') {
        return false;
      }
      return 2000;
    },
  });
}

export function useCancelExport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (jobId: string) => GoogleAdsService.cancelExport(jobId),
    onSuccess: (_, jobId) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.exportProgress(jobId) });
    },
  });
}

// The export endpoints return a pre-signed S3 URL. Navigating to it directly keeps
// the request out of XHR, so no S3 CORS setup is needed and Content-Disposition
// makes the browser save the file.
function triggerBrowserDownload({ downloadUrl, fileName }: ExportDownload) {
  const a = document.createElement('a');
  a.href = downloadUrl;
  a.download = fileName;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

export function useDownloadExport() {
  return useMutation({
    mutationFn: (jobId: string) => GoogleAdsService.downloadExport(jobId),
    onSuccess: triggerBrowserDownload,
  });
}

export function useQuickExport() {
  return useMutation({
    mutationFn: ({ customerId, dateRange }: { customerId: string; dateRange: string }) =>
      GoogleAdsService.quickExport(customerId, dateRange),
    onSuccess: triggerBrowserDownload,
  });
}
